// Campaign lifecycle + segments — /api/admin/v2/campaigns + /segments

export type LifecycleChannel = 'in_app' | 'email' | 'sms' | 'whatsapp';

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'running'
  | 'paused'
  | 'completed'
  | 'canceled'
  | 'failed'
  | string;

export interface LifecyclePagination {
  total: number;
  limit: number;
  offset: number;
  has_more?: boolean;
}

export interface SegmentFilters {
  country_iso2?: string[];
  locale?: string[];
  gender?: string[];
  active?: boolean | null;
  tester?: boolean | null;
  admin_hold?: boolean | null;
  email_verified?: boolean | null;
  phone_verified?: boolean | null;
  age_min?: number | null;
  age_max?: number | null;
  created_after?: number | null;
  created_before?: number | null;
  roles?: string[];
  org_ids?: number[];
  has_listing?: boolean | null;
  tags?: number[];
  last_active_days?: number | null;
}

export interface AudienceSampleUser {
  uid?: number;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  locale?: string | null;
}

export type SegmentSource = 'users' | 'waitlist' | 'prospects' | 'upload';

/** waitlist: campaign_type / interest; prospects: stages / kind; upload: csv_ref. */
export interface SegmentSourceParams {
  campaign_type?: string | string[];
  interest?: string | string[];
  stages?: string[];
  kind?: string | string[];
  csv_columns?: string[];
  csv_ref?: string;
  csv_name?: string;
  csv_count?: number;
}

export interface Segment {
  segment_id?: string;
  sid?: string;
  name: string;
  description?: string | null;
  source?: SegmentSource;
  source_params?: SegmentSourceParams;
  filters: SegmentFilters;
  estimated_size?: number | null;
  created_at?: number | string;
  updated_at?: number | string;
}

export interface SegmentsListResponse {
  segments?: Segment[];
  pagination?: LifecyclePagination;
}

export interface SegmentPreviewResult {
  estimated_size?: number;
  sample?: AudienceSampleUser[];
}

export interface CreateSegmentPayload {
  name: string;
  description?: string;
  source?: SegmentSource;
  source_params?: SegmentSourceParams;
  filters: SegmentFilters;
}

export interface UpdateSegmentPayload {
  name?: string;
  description?: string | null;
  source?: SegmentSource;
  source_params?: SegmentSourceParams;
  filters?: SegmentFilters;
}

export interface CsvUploadResult {
  csv_ref?: string;
  skipped?: number;
  skipped_lines?: number[];
  /** Columns found; anything beyond email/phone/uid/name is a {{placeholder}}. */
  columns?: string[];
  /** Rows kept (the API's name). */
  count?: number;
  total?: number;
  async?: boolean;
  task_id?: string;
}

export interface CampaignWhatsAppContent {
  template_name?: string;
  language?: string;
  components?: unknown[];
}

export interface CampaignContent {
  subject?: string;
  body?: string;
  html?: string;
  /** Mailbox the email goes out from (GET messaging/mailboxes); unset = the default sender. */
  from_email?: string;
  /** Add the mailbox's signature (default true). */
  add_signature?: boolean;
  messaging_ntmplid?: number | null;
  whatsapp?: CampaignWhatsAppContent | null;
}

export interface CampaignAudience {
  segment_ids?: string[];
  exclude_segment_ids?: string[];
  ad_hoc?: Array<{ uid?: number; email?: string; phone?: string }>;
  csv_ref?: string | null;
}

export interface CampaignSchedule {
  mode?: 'now' | 'scheduled' | string;
  send_at?: number | null;
}

export interface CampaignProgress {
  total?: number;
  processed?: number;
  sent?: number;
  failed?: number;
  opted_out?: number;
  suppressed?: number;
  no_address?: number;
  skipped?: number;
}

export interface ChannelMetrics {
  sent?: number;
  failed?: number;
  opted_out?: number;
  suppressed?: number;
  no_address?: number;
  skipped?: number;
}

export interface Campaign {
  campaign_id: string;
  name: string;
  status: CampaignStatus;
  channels?: LifecycleChannel[];
  content?: CampaignContent;
  audience?: CampaignAudience;
  respect_prefs?: boolean;
  schedule?: CampaignSchedule;
  rate_limit_per_min?: number;
  progress?: CampaignProgress;
  metrics_by_channel?: Partial<Record<LifecycleChannel, ChannelMetrics>>;
  started_at?: number | string | null;
  finished_at?: number | string | null;
  created_at?: number | string;
  updated_at?: number | string;
  override_denied?: boolean;
}

export interface CampaignsListResponse {
  campaigns?: Campaign[];
  pagination?: LifecyclePagination;
}

export interface CampaignListParams {
  status?: CampaignStatus | string;
  channel?: LifecycleChannel | string;
  date_from?: number;
  date_to?: number;
  limit?: number;
  offset?: number;
}

export interface CampaignDetailResponse {
  campaign: Campaign;
}

export interface CreateCampaignPayload {
  name: string;
  channels: LifecycleChannel[];
  content: CampaignContent;
  audience: CampaignAudience;
  respect_prefs?: boolean;
  schedule?: CampaignSchedule;
  rate_limit_per_min?: number;
}

export type UpdateCampaignPayload = Partial<CreateCampaignPayload>;

export interface StartCampaignPayload {
  send_at?: number;
  /** Start even though some recipients lack data for a placeholder (it renders blank). */
  allow_missing_fields?: boolean;
}

export interface StartCampaignResult {
  success?: boolean;
  status?: CampaignStatus;
  scheduled?: boolean;
  send_at?: number | null;
  override_denied?: boolean;
}

export interface CampaignRecipient {
  uid?: number | null;
  email?: string | null;
  phone?: string | null;
  status?: string;
  channels?: Partial<Record<LifecycleChannel, { status?: string }>>;
}

export interface CampaignRecipientsResponse {
  recipients?: CampaignRecipient[];
  pagination?: LifecyclePagination;
}

export interface CampaignRecipientsParams {
  status?: string;
  limit?: number;
  offset?: number;
}

/** Per placeholder in the campaign copy: how many recipients lack the data. */
export interface MergeReport {
  fields: Record<string, { fallback: string | null; missing: number; total: number; blocking: boolean }>;
  blocking: string[];
}

export interface SegmentMember {
  uid?: number | null;
  name?: string | null;
  first_name?: string | null;
  email?: string | null;
  phone?: string | null;
  fields?: Record<string, string>;
}

export interface SegmentMembersResult {
  members: SegmentMember[];
  total: number;
  limit: number;
  offset: number;
  columns: string[];
  source: SegmentSource;
}

export interface AudiencePreviewResult {
  estimated_size?: number;
  sample?: AudienceSampleUser[];
  merge?: MergeReport;
}

export interface OptionCount<K = string> {
  key: K;
  label?: string;
  count: number;
}

export interface SegmentOptions {
  users: {
    countries: OptionCount[];
    locales: OptionCount[];
    genders: OptionCount[];
    roles: OptionCount[];
    tags: OptionCount<number>[];
    orgs: OptionCount<number>[];
  };
  waitlist: { campaign_type: OptionCount[]; interest: OptionCount[] };
  prospects: { stages: OptionCount[]; kinds: OptionCount[] };
}

export const CAMPAIGN_STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-blue-50 text-blue-700',
  running: 'bg-emerald-50 text-emerald-700',
  paused: 'bg-amber-50 text-amber-800',
  completed: 'bg-slate-100 text-slate-700',
  canceled: 'bg-orange-50 text-orange-800',
  failed: 'bg-red-50 text-red-700',
};

export const segmentId = (s: Segment): string =>
  String(s.segment_id || s.sid || '');
